var _effectorFileName = "/src/shared/components/contact-form/contact-form.tsx";
import Link from 'next/link';
import { useRouter } from 'next/router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/shared/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { eventGA } from '@/shared/lib/analytics/google';
import { eventYM } from '@/shared/lib/analytics/yandex';
import { cn } from '@/shared/lib/cn';
import { TextShimmerAnimation } from '../animation/text-shimmer';
import ArrowIcon from '../icons/arrow-icon';
import AtIcon from '../icons/at-icon';
import { Checkbox } from '../ui/checkbox';
import { ContactFormSchema, contactFormSchema } from './schema';
import { ContactFormData, EntryPoint } from './types';
interface ContactFormProps {
  className?: string;
  customButton?: React.ReactNode;
  entryPoint?: EntryPoint;
  buttonText?: string;
  onFormSubmit?: (data: ContactFormData) => void;
}
export const ContactForm = (props: ContactFormProps) => {
  const {
    className,
    customButton,
    entryPoint = 'DEFAULT',
    buttonText,
    onFormSubmit
  } = props;
  const router = useRouter();
  const form = useForm<ContactFormSchema>({
    resolver: zodResolver(contactFormSchema()),
    mode: 'onBlur',
    defaultValues: {
      telegram: '',
      isAgreeWithPersonalDataProcessing: false
    }
  });
  const {
    formState,
    handleSubmit,
    reset
  } = form;
  const onSubmit = (values: z.infer<ReturnType<typeof contactFormSchema>>) => {
    const {
      telegram
    } = values;
    const contactFormData: ContactFormData = {
      sentFrom: entryPoint,
      url: router.asPath,
      telegram
    };
    return new Promise<void>(resolve => {
      setTimeout(() => {
        onFormSubmit?.(contactFormData);
        eventYM('lead');
        eventGA('lead');
        reset();
        resolve();
      }, 500);
    });
  };
  return <Form {...form}>
      <form className={cn('max-w-5xl space-y-4', className)} onSubmit={handleSubmit(onSubmit)}>
        <FormField control={form.control} name="telegram" render={({
        field
      }) => <FormItem className="flex flex-col">
              <FormLabel>
                Telegram<sup className="text-red-600">*</sup>
              </FormLabel>

              <FormControl>
                <Input {...field} placeholder="Ваш тег или номер телефона" startAdornment={<AtIcon className="h-5 w-5" />} className="text-base sm:text-sm" />
              </FormControl>

              <FormMessage className="font-normal" />
            </FormItem>} />

        <FormField control={form.control} name="isAgreeWithPersonalDataProcessing" render={({
        field
      }) => <div>
              <FormItem className="flex items-start gap-3 rounded-lg border border-gray-300 p-3 hover:bg-accent/50 has-[[aria-checked=true]]:border-brand-accent-normal has-[[aria-checked=true]]:bg-accent/50 dark:border-gray-500 dark:has-[[aria-checked=true]]:border-brand-accent-normal dark:has-[[aria-checked=true]]:bg-accent/50">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={isChecked => field.onChange(isChecked)} className="data-[state=checked]:border-brand-accent-dark data-[state=checked]:bg-brand-accent-dark data-[state=checked]:text-white dark:data-[state=checked]:border-brand-accent-normal dark:data-[state=checked]:bg-brand-accent-normal" />
                </FormControl>

                <FormLabel className="!mt-0">
                  Подтверждаю, что согласен(-а) на{' '}
                  <Button variant="link" size="link-sm" className="mt-1 text-wrap text-left text-muted-foreground" onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
            }}>
                    <Link href="/agreement" target="_blank" className="text-sm font-normal leading-tight">
                      обработку персональных данных
                    </Link>
                  </Button>
                </FormLabel>
              </FormItem>

              <FormMessage className="font-normal" />
            </div>} />

        <div>
          <hr className="m-0 mb-4 h-px w-full border-none bg-gradient-to-r from-neutral-200/0 via-neutral-500/30 to-neutral-200/0" />

          {customButton && <Button disabled={formState.isSubmitting} loading={formState.isSubmitting} size="link" type="submit" className="group mx-auto mt-4  flex items-center justify-center rounded-[2rem] border border-brand-accent-light bg-background px-6 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50" aria-label="Отправить контактную форму">
              <TextShimmerAnimation className="flex items-center justify-center">
                {buttonText || 'Отправить'}
                <ArrowIcon className="ml-1 size-4 transition-all duration-300 ease-out group-hover:translate-x-1" />
              </TextShimmerAnimation>
            </Button>}

          {!customButton && <Button disabled={formState.isSubmitting} loading={formState.isSubmitting} variant="cta" type="submit" className="mx-auto block tracking-wide" aria-label="Отправить контактную форму">
              {buttonText || 'Отправить'}
            </Button>}
        </div>
      </form>
    </Form>;
};